import { WASH_TYPES } from '@/lib/locatieTypes';
import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { EmailVoorkeuren } from '@/pages/MijnEmailInstellingen';
import { Loader2, Pencil, Trash2, KeyRound, Users, Shield, ShieldAlert, ShieldCheck, User, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const ROLE_CONFIG = {
  admin:           { label: 'Admin',           level: 4, icon: ShieldAlert, color: 'bg-red-100 text-red-700' },
  manager:         { label: 'Manager',         level: 3, icon: ShieldCheck, color: 'bg-purple-100 text-purple-700' },
  bedrijfsleider:  { label: 'Bedrijfsleider',  level: 2, icon: Shield,      color: 'bg-blue-100 text-blue-700' },
  medewerker:      { label: 'Medewerker',      level: 1, icon: User,        color: 'bg-slate-100 text-slate-700' },
};

const ROLES_ORDERED = ['admin', 'manager', 'bedrijfsleider', 'medewerker'];

// ── User Form Modal ─────────────────────────────────────────────────────────
const UserFormModal = ({ user, locaties, onClose, onSaved, currentUser }) => {
  const isEdit = !!user;
  const { isAdmin, canManageUser, maxAssignableRole, getRoleLevel } = useAuth();

  const [form, setForm] = useState({
    email: user?.email || '',
    naam: user?.naam || '',
    telefoon: user?.telefoon || '',
    personeelsnummer: user?.personeelsnummer || '',
    rol: user?.rol || 'medewerker',
    locaties: user?.locaties || [],
    verantwoordelijke_locaties: user?.verantwoordelijke_locaties || [],
    thuislocatie_id: user?.thuislocatie_id || '',
    actief: user?.actief ?? true,
    wachtwoord: ''
  });
  const [saving, setSaving] = useState(false);
  // E10: niets tonen of schakelen vóór de echte voorkeuren binnen zijn, en een
  // mislukte opslag draait terug.
  const [emailVoorkeuren, setEmailVoorkeuren] = useState(null);
  const [voorkeurBezig, setVoorkeurBezig] = useState(false);
  const [voorkeurFout, setVoorkeurFout] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let actueel = true;
    fetch(`${API}/v2/email/voorkeuren/${user.id}`, { headers: hdrs() })
      .then(r => { if (!r.ok) throw new Error('laden mislukt'); return r.json(); })
      .then(d => { if (actueel) { setEmailVoorkeuren(d || {}); setVoorkeurFout(false); } })
      .catch(() => { if (actueel) { setVoorkeurFout(true); setEmailVoorkeuren({}); } });
    return () => { actueel = false; };
  }, [user?.id]);

  const bewaarVoorkeuren = async (nieuw) => {
    const vorige = emailVoorkeuren;
    setEmailVoorkeuren(nieuw);
    setVoorkeurBezig(true);
    try {
      const res = await fetch(`${API}/v2/email/voorkeuren/${user.id}`, {
        method: 'PUT', headers: hdrs(), body: JSON.stringify(nieuw),
      });
      if (!res.ok) throw new Error('opslaan mislukt');
      setEmailVoorkeuren(await res.json());
    } catch {
      setEmailVoorkeuren(vorige);
      toast.error('E-mailvoorkeuren opslaan mislukt');
    } finally {
      setVoorkeurBezig(false);
    }
  };

  // Which roles can the current user assign?
  const maxRole = maxAssignableRole();
  const maxLevel = getRoleLevel(maxRole);
  const assignableRoles = ROLES_ORDERED.filter(r => getRoleLevel(r) <= maxLevel);

  // Can we edit this user at all?
  const canEdit = !isEdit || canManageUser(user?.rol);

  const handleSubmit = async () => {
    if (!form.naam) {
      toast.error('Naam is verplicht');
      return;
    }
    setSaving(true);
    try {
      const url = `${API}/gebruikers/${user.id}`;
      // Backend accepteert sinds v18.1 ook rol + locaties (Identity is auth-only)
      const body = {
        naam: form.naam,
        telefoon: form.telefoon || null,
        personeelsnummer: form.personeelsnummer || null,
        rol: form.rol,
        locaties: form.locaties,
        verantwoordelijke_locaties: form.verantwoordelijke_locaties,
        thuislocatie_id: form.thuislocatie_id || null,
        actief: form.actief,
      };

      const res = await fetch(url, { method: 'PUT', headers: hdrs(), body: JSON.stringify(body) });
      if (res.ok) {
        toast.success('Gebruiker bijgewerkt');
        onSaved?.();
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Opslaan mislukt');
      }
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const toggleLocatie = (locId) => {
    setForm(p => ({
      ...p,
      locaties: p.locaties.includes(locId)
        ? p.locaties.filter(l => l !== locId)
        : [...p.locaties, locId]
    }));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-form-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Gebruiker bewerken' : 'Nieuwe gebruiker'}</DialogTitle>
        </DialogHeader>

        {!canEdit ? (
          <div className="py-4 text-center text-slate-500">
            Je hebt geen rechten om deze gebruiker te bewerken.
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Naam *</Label>
                <Input value={form.naam} onChange={(e) => setForm(p => ({ ...p, naam: e.target.value }))} data-testid="user-naam" />
              </div>
              <div>
                <Label>Email{isEdit ? '' : ' *'}</Label>
                {isEdit ? (
                  <>
                    <Input type="email" value={form.email} readOnly disabled
                      className="bg-slate-50 text-slate-500" data-testid="user-email" />
                    <p className="text-xs text-slate-400 mt-1" data-testid="user-email-uitleg">
                      E-mail komt uit TW1 Identity
                    </p>
                  </>
                ) : (
                  <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} data-testid="user-email" />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefoon</Label>
                <Input value={form.telefoon} onChange={(e) => setForm(p => ({ ...p, telefoon: e.target.value }))} data-testid="user-telefoon" />
              </div>
              <div>
                <Label>Personeelsnummer</Label>
                <Input value={form.personeelsnummer} onChange={(e) => setForm(p => ({ ...p, personeelsnummer: e.target.value }))} placeholder="bv. TW1-00042" data-testid="user-personeelsnummer" />
              </div>
            </div>

            {!isEdit && null}

            <div>
              <Label>Rol</Label>
              <Select value={form.rol} onValueChange={(v) => setForm(p => ({ ...p, rol: v }))}>
                <SelectTrigger data-testid="user-rol-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {assignableRoles.map(r => {
                    const cfg = ROLE_CONFIG[r];
                    return (
                      <SelectItem key={r} value={r}>
                        <span className="flex items-center gap-2">
                          <cfg.icon className="w-4 h-4" />
                          {cfg.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Thuislocatie */}
            <div>
              <Label>Thuislocatie <span className="text-xs text-red-400 ml-1">*</span></Label>
              <Select value={form.thuislocatie_id} onValueChange={(v) => setForm(p => ({ ...p, thuislocatie_id: v }))}>
                <SelectTrigger data-testid="user-thuislocatie-select">
                  <SelectValue placeholder="Selecteer thuislocatie..." />
                </SelectTrigger>
                <SelectContent>
                  {locaties.map(loc => (
                    <SelectItem key={loc._id} value={loc._id}>{loc.naam}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">De locatie waar deze gebruiker fysiek werkt</p>
            </div>

            {/* Locatie-scope for Managers (Admin only) */}
            {form.rol === 'manager' && isAdmin() && (
              <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Locatie-scope</Label>
                    <p className="text-xs text-slate-400">Bepaalt welke locaties deze manager kan zien</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={form.locaties.length === 0}
                      onCheckedChange={(v) => setForm(p => ({ ...p, locaties: v ? [] : [p.thuislocatie_id].filter(Boolean) }))}
                      data-testid="manager-alle-locaties-toggle"
                    />
                    <span className="text-xs text-slate-600">{form.locaties.length === 0 ? 'Alle locaties' : 'Beperkt'}</span>
                  </div>
                </div>
                {form.locaties.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1 bg-white">
                    {locaties.map(loc => (
                      <label key={loc._id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={form.locaties.includes(loc._id)}
                          onChange={() => toggleLocatie(loc._id)} className="rounded border-slate-300" />
                        <span className="text-sm">{loc.naam}</span>
                      </label>
                    ))}
                    <p className="text-xs text-slate-500 pt-1">{form.locaties.length} locatie(s) geselecteerd</p>
                  </div>
                )}
              </div>
            )}

            {/* Locatie-toewijzing for BL/Medewerker */}
            {(form.rol === 'bedrijfsleider' || form.rol === 'medewerker') && (
              <div>
                <Label>Locaties</Label>
                <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1" data-testid="user-locaties-edit">
                  {locaties.map(loc => (
                    <label key={loc._id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.locaties.includes(loc._id)}
                        onChange={() => toggleLocatie(loc._id)}
                        className="rounded border-slate-300"
                        data-testid={`user-locatie-${loc._id}`}
                      />
                      <span className="text-sm">{loc.naam}</span>
                    </label>
                  ))}
                </div>
                {form.locaties.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">{form.locaties.length} locatie(s) geselecteerd</p>
                )}
              </div>
            )}

            {/* Verantwoordelijke locaties — werkgebied voor admin/manager (Uitgebreide Analyse) */}
            {(form.rol === 'admin' || form.rol === 'manager') && (
              <div data-testid="verantwoordelijke-locaties-section">
                <Label className="flex items-center gap-2">
                  Verantwoordelijke locaties (werkgebied)
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Truckwash locaties waarvan performance data getoond wordt bij de uitgebreide analyse.
                </p>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                  {locaties
                    .filter(l => WASH_TYPES.includes(l.type))
                    .slice()
                    .sort((a, b) => a.naam.localeCompare(b.naam))
                    .map(loc => (
                      <label
                        key={loc._id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.verantwoordelijke_locaties.includes(loc.slug)}
                          onChange={() => setForm(p => ({
                            ...p,
                            verantwoordelijke_locaties: p.verantwoordelijke_locaties.includes(loc.slug)
                              ? p.verantwoordelijke_locaties.filter(s => s !== loc.slug)
                              : [...p.verantwoordelijke_locaties, loc.slug],
                          }))}
                          className="rounded border-slate-300"
                          data-testid={`verant-loc-${loc.slug}`}
                        />
                        <span className="text-sm">{loc.naam}</span>
                      </label>
                    ))}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-500">
                    {form.verantwoordelijke_locaties.length} locatie(s) geselecteerd
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setForm(p => ({
                        ...p,
                        verantwoordelijke_locaties: locaties.filter(l => WASH_TYPES.includes(l.type)).map(l => l.slug),
                      }))}
                      className="text-xs text-[#1f2548] hover:underline"
                      data-testid="verant-loc-select-all"
                    >
                      Alle selecteren
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, verantwoordelijke_locaties: [] }))}
                      className="text-xs text-slate-500 hover:underline"
                      data-testid="verant-loc-deselect-all"
                    >
                      Wissen
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isEdit && canEdit && (
              <div className="border-t border-slate-100 pt-4" data-testid="user-email-voorkeuren">
                <Label className="mb-2 block">E-mailmeldingen</Label>
                {emailVoorkeuren === null ? (
                  <p className="text-xs text-slate-400" data-testid="user-voorkeur-laden">
                    Voorkeuren laden...
                  </p>
                ) : (
                  <>
                    {voorkeurFout && (
                      <p className="text-xs text-red-600 mb-2" data-testid="user-voorkeur-fout">
                        Voorkeuren konden niet worden geladen — wijzigen is uitgeschakeld.
                      </p>
                    )}
                    <EmailVoorkeuren waarde={emailVoorkeuren} onWijzig={bewaarVoorkeuren}
                      geblokkeerd={voorkeurFout || voorkeurBezig}
                      testPrefix="user-voorkeur" />
                  </>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch
                id="actief"
                checked={form.actief}
                onCheckedChange={(v) => setForm(p => ({ ...p, actief: v }))}
                data-testid="user-actief-switch"
              />
              <Label htmlFor="actief">Account actief</Label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          {canEdit && (
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="user-form-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {isEdit ? 'Opslaan' : 'Aanmaken'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────
const Gebruikersbeheer = () => {
  const { user: currentUser, canManageUser, isAdmin } = useAuth();
  const [gebruikers, setGebruikers] = useState([]);
  const [locaties, setLocaties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [filterRol, setFilterRol] = useState('alle');
  const [filterActief, setFilterActief] = useState('alle');
  const [filterLocatie, setFilterLocatie] = useState('alle');
  const [cacheBusy, setCacheBusy] = useState(false);

  const handleCacheLeegmaken = async () => {
    if (!window.confirm('Validate-cache leegmaken? Alle ingelogde gebruikers krijgen op hun volgende request een verse Identity-validatie. Gebruik dit alleen bij gerapporteerde rechten-inconsistenties.')) return;
    setCacheBusy(true);
    try {
      const res = await fetch(`${API}/auth/cache-leegmaken`, { method: 'POST', headers: hdrs() });
      if (res.ok) toast.success('Cache geleegd');
      else toast.error('Cache leegmaken mislukt');
    } catch {
      toast.error('Cache leegmaken mislukt');
    } finally {
      setCacheBusy(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, locsRes] = await Promise.all([
        fetch(`${API}/gebruikers`, { headers: hdrs() }),
        fetch(`${API}/locaties`, { headers: hdrs() })
      ]);
      if (usersRes.ok) setGebruikers(await usersRes.json());
      if (locsRes.ok) setLocaties(await locsRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      const res = await fetch(`${API}/gebruikers/${deleteUser.id}`, {
        method: 'DELETE',
        headers: hdrs()
      });
      if (res.ok) {
        toast.success('Gebruiker verwijderd');
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Verwijderen mislukt');
      }
    } catch {
      toast.error('Verwijderen mislukt');
    } finally {
      setDeleteUser(null);
    }
  };

  const getLocatieNamen = (locatieIds) => {
    if (!locatieIds || locatieIds.length === 0) return 'Alle locaties';
    return locatieIds
      .map(id => locaties.find(l => l._id === id)?.naam || id)
      .join(', ');
  };

  const filtered = gebruikers.filter(u => {
    if (filterRol !== 'alle' && u.rol !== filterRol) return false;
    if (filterActief === 'actief' && !u.actief) return false;
    if (filterActief === 'inactief' && u.actief) return false;
    if (filterLocatie !== 'alle' && u.thuislocatie_id !== filterLocatie) return false;
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 sm:px-0" data-testid="gebruikersbeheer-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="gebruikersbeheer-title">
            <Users className="w-6 h-6" /> Gebruikersbeheer
          </h1>
          <p className="text-slate-500">{gebruikers.length} gebruikers</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={filterRol} onValueChange={setFilterRol}>
            <SelectTrigger className="w-[140px]" data-testid="filter-rol">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle rollen</SelectItem>
              {ROLES_ORDERED.map(r => (
                <SelectItem key={r} value={r}>{ROLE_CONFIG[r].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterActief} onValueChange={setFilterActief}>
            <SelectTrigger className="w-[130px]" data-testid="filter-actief">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Actief + Inactief</SelectItem>
              <SelectItem value="actief">Alleen actief</SelectItem>
              <SelectItem value="inactief">Alleen inactief</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterLocatie} onValueChange={setFilterLocatie}>
            <SelectTrigger className="w-[160px]" data-testid="filter-locatie">
              <SelectValue placeholder="Locatie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle locaties</SelectItem>
              {locaties.map(l => <SelectItem key={l._id} value={l._id}>{l.naam}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleCacheLeegmaken} variant="outline" size="sm" disabled={cacheBusy} data-testid="cache-leegmaken-btn" title="Forceer alle ingelogde users om opnieuw te valideren bij Identity Platform — gebruiken bij inconsistenties.">
            <RefreshCw className={`w-4 h-4 mr-2 ${cacheBusy ? 'animate-spin' : ''}`} />
            Cache leegmaken
          </Button>
          <Button onClick={() => window.open('https://id.truckwash1.cloud/admin', '_blank')} variant="outline" size="sm" data-testid="open-identity-btn">
            <Users className="w-4 h-4 mr-2" /> Identity Platform
          </Button>
        </div>
      </div>

      {/* Identity-auth-only notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="identity-notice">
        <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-900">
          <strong>Authenticatie loopt via TW1 Identity Platform. Rollen en locaties beheer je hier in Truckwash-Ops.</strong>
          <p className="text-xs text-blue-700 mt-0.5">
            Nieuwe gebruikers worden automatisch aangemaakt met rol "medewerker" zodra ze voor het eerst inloggen — admin promoot ze hieronder.
          </p>
        </div>
      </div>

      {/* Role summary */}
      <div className="flex gap-2 flex-wrap">
        {ROLES_ORDERED.map(r => {
          const count = gebruikers.filter(u => u.rol === r).length;
          const cfg = ROLE_CONFIG[r];
          return (
            <Badge key={r} className={`${cfg.color} text-xs gap-1`} data-testid={`count-${r}`}>
              <cfg.icon className="w-3 h-3" />
              {count} {cfg.label}
            </Badge>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto" data-testid="gebruikers-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-3 font-semibold text-slate-700 text-xs">Nr.</th>
                <th className="py-3 px-3 font-semibold text-slate-700 text-xs">Naam</th>
                <th className="py-3 px-3 font-semibold text-slate-700 text-xs">Email</th>
                <th className="py-3 px-3 font-semibold text-slate-700 text-xs">Telefoon</th>
                <th className="py-3 px-3 font-semibold text-slate-700 text-xs">Rol</th>
                <th className="py-3 px-3 font-semibold text-slate-700 text-xs">Locatie</th>
                <th className="py-3 px-3 font-semibold text-slate-700 text-xs">In dienst</th>
                <th className="py-3 px-3 font-semibold text-slate-700 text-xs text-center">Status</th>
                <th className="py-3 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((u) => {
                const cfg = ROLE_CONFIG[u.rol] || ROLE_CONFIG.medewerker;
                const canManage = canManageUser(u.rol);
                const isSelf = u.id === currentUser?.id;

                return (
                  <tr key={u.id} className={`hover:bg-slate-50 ${!u.actief ? 'opacity-50' : ''}`} data-testid={`user-row-${u.id}`}>
                    <td className="py-2.5 px-3 text-xs text-slate-400 font-mono">{u.personeelsnummer || '-'}</td>
                    <td className="py-2.5 px-3">
                      <span className="font-medium text-slate-700 text-sm">{u.naam}</span>
                    </td>
                    <td className="py-2.5 px-3 text-slate-600 text-xs">{u.email}</td>
                    <td className="py-2.5 px-3 text-slate-500 text-xs">{u.telefoon || '-'}</td>
                    <td className="py-2.5 px-3">
                      <Badge className={`text-xs ${cfg.color}`}>
                        <cfg.icon className="w-3 h-3 mr-1" />
                        {cfg.label}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-3 text-slate-600 text-xs">
                      {u.thuislocatie_id ? getLocatieNamen([u.thuislocatie_id]) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-slate-400">{u.datum_in_dienst || '-'}</td>
                    <td className="py-2.5 px-3 text-center">
                      {u.actief ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Actief</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-xs">Inactief</Badge>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-1">
                        {canManage && (
                          <>
                            <Button size="icon" variant="ghost" className="w-7 h-7 text-slate-400 hover:text-[#1f2548]" onClick={() => setEditUser(u)} data-testid={`edit-user-${u.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="w-7 h-7 text-slate-400 hover:text-blue-600"
                              onClick={() => window.open('https://id.truckwash1.cloud/admin', '_blank')} title="Wachtwoord en rol beheren in Identity Platform" data-testid={`identity-link-${u.id}`}>
                              <KeyRound className="w-3 h-3" />
                            </Button>
                            {!isSelf && isAdmin() && (
                              <Button size="icon" variant="ghost" className="w-7 h-7 text-slate-400 hover:text-red-500" onClick={() => setDeleteUser(u)} data-testid={`delete-user-${u.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-slate-400">
                    Geen gebruikers gevonden
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {(showForm || editUser) && (
        <UserFormModal
          user={editUser}
          locaties={locaties}
          currentUser={currentUser}
          onClose={() => { setShowForm(false); setEditUser(null); }}
          onSaved={fetchData}
        />
      )}

      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Gebruiker verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{deleteUser?.naam}" ({deleteUser?.email}) wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-user-confirm">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Gebruikersbeheer;
